import { useCallback, useEffect, useState } from 'react';
import { fabric } from 'fabric';
import { CanvasDataTypes } from '../utils/canvas-utils/canvas-data-types';

type TooltipState = {
  isTooltipOpen: boolean;
  tooltipPosition: { x: number; y: number };
  closeTooltip: () => void;
};

/**
 * Hook to manage the low-resolution icon tooltip on the fabric canvas.
 * Desktop: shows tooltip on hover over the warning icon.
 * Mobile: shows tooltip on tap of the warning icon.
 */
export const useLowResIconTooltip = (canvasRef: React.RefObject<fabric.Canvas>, isMobile: boolean): TooltipState => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const closeTooltip = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const isLowResIcon = useCallback(
    (target: fabric.Object | undefined): boolean => target?.data?.type === CanvasDataTypes.PhotoIcon,
    [],
  );

  const getTooltipPosition = useCallback((icon: fabric.Object, canvas: fabric.Canvas): { x: number; y: number } => {
    const zoom = canvas.getZoom();
    const iconLeft = (icon.left ?? 0) * zoom;
    const iconTop = (icon.top ?? 0) * zoom;
    const iconHeight = (icon.getScaledHeight?.() ?? 40) * zoom;

    return {
      x: iconLeft,
      y: iconTop + iconHeight + 2,
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const handleMouseOver = (e: fabric.IEvent) => {
      if (isMobile) return;
      if (isLowResIcon(e.target)) {
        const pos = getTooltipPosition(e.target as fabric.Object, canvas);
        setTooltipPosition(pos);
        setIsTooltipOpen(true);
      }
    };

    const handleMouseOut = (e: fabric.IEvent) => {
      if (isMobile) return;
      if (isLowResIcon(e.target)) {
        setIsTooltipOpen(false);
      }
    };

    const handleMouseDown = (e: fabric.IEvent) => {
      if (!isMobile) return;
      if (isLowResIcon(e.target)) {
        const pos = getTooltipPosition(e.target as fabric.Object, canvas);
        setTooltipPosition(pos);
        setIsTooltipOpen((prev) => !prev);
      } else {
        setIsTooltipOpen(false);
      }
    };

    canvas.on('mouse:over', handleMouseOver);
    canvas.on('mouse:out', handleMouseOut);
    canvas.on('mouse:down', handleMouseDown);

    return () => {
      canvas.off('mouse:over', handleMouseOver);
      canvas.off('mouse:out', handleMouseOut);
      canvas.off('mouse:down', handleMouseDown);
    };
  }, [canvasRef, isMobile, isLowResIcon, getTooltipPosition]);

  return { isTooltipOpen, tooltipPosition, closeTooltip };
};
